import { Address, SuggestedAddress } from '@varo/graphql-schema-types';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import Button from 'shared/components/vxl/Button/Button';
import BodyBaseBoldText from 'shared/components/vxl/typography/primary/body/BodyBaseBoldText';
import BodyBaseText from 'shared/components/vxl/typography/primary/body/BodyBaseText';
import TitleMdText from 'shared/components/vxl/typography/primary/title/TitleMdText';
import { AddressView } from 'shared/hooks/useValidateAddress/useValidateAddress';
import theme from 'shared/styles/theme';

interface AddressValidationProps {
  handleNext: () => void;
  closeBottomSheet: () => void;
  fullAddress?: SuggestedAddress;
  addressType?: string;
  isLoading?: boolean;
}

const AddressValidation = ({
  handleNext,
  closeBottomSheet,
  fullAddress,
  addressType,
  isLoading,
}: AddressValidationProps) => {
  const handlePress = () => {
    handleNext();
  };

  const { t: translatedText } = useTranslation('signup', {
    keyPrefix: 'address',
  });

  const getAddressText = (address: Address): string => {
    return address
      ? `${address.addressLine1}, ${address.addressLine2 ?? ''} \n ${address.city}, ${address.state} ${address.zip}`
      : '';
  };

  if (addressType === AddressView.InvalidAddress) {
    return (
      <View style={styles.bottomSheet}>
        <TitleMdText numberOfLines={0} style={styles.bottomSheetTitle}>
          {translatedText('validations.invalidAddress.header')}
        </TitleMdText>
        <BodyBaseBoldText numberOfLines={0} style={styles.bottomSheetTitle}>
          {translatedText('validations.whatYouEntered') + ':'}
        </BodyBaseBoldText>
        <View style={styles.bottomSheetTitle}>
          <BodyBaseText numberOfLines={0} style={styles.bottomSheetTitle}>
            {getAddressText(fullAddress?.originalAddress as Address)}
          </BodyBaseText>
        </View>
        <View style={styles.bottomSheetTitle}>
          <BodyBaseText
            numberOfLines={0}
            style={[styles.bottomSheetTitle, styles.warningColor]}
          >
            {fullAddress?.message}
          </BodyBaseText>
        </View>
        <View style={styles.bottomSheetBody}>
          <Button
            label={translatedText('buttons.editAddress')}
            type="SECONDARY"
            analyticsId="editInvalidAddressClicked"
            onPress={closeBottomSheet}
          />
        </View>
      </View>
    );
  }
  return (
    <View style={styles.bottomSheet}>
      <TitleMdText numberOfLines={0} style={styles.bottomSheetTitle}>
        {translatedText('validations.suggestedAddress.header')}
      </TitleMdText>
      <BodyBaseText numberOfLines={0} style={styles.bottomSheetTitle}>
        {translatedText('validations.suggestedAddress.subHeader')}
      </BodyBaseText>
      <BodyBaseBoldText numberOfLines={0} style={styles.bottomSheetTitle}>
        {translatedText('validations.suggested')}
      </BodyBaseBoldText>
      <View style={styles.bottomSheetTitle}>
        <BodyBaseText numberOfLines={0}>
          {getAddressText(fullAddress?.suggestedAddress as Address)}
        </BodyBaseText>
      </View>
      <BodyBaseBoldText numberOfLines={0} style={styles.bottomSheetTitle}>
        {translatedText('validations.whatYouEntered')}
      </BodyBaseBoldText>
      <View style={styles.bottomSheetTitle}>
        <BodyBaseText numberOfLines={0}>
          {getAddressText(fullAddress?.originalAddress as Address)}
        </BodyBaseText>
      </View>
      <View style={styles.bottomSheetBody}>
        <Button
          label={translatedText('validations.next')}
          type="PRIMARY"
          onPress={handlePress}
          isLoading={isLoading}
        />
      </View>
      <View style={styles.bottomSheetBody}>
        <Button
          label={translatedText('buttons.editAddress')}
          type="SECONDARY"
          analyticsId="editAddressClicked"
          onPress={closeBottomSheet}
        />
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  bottomSheet: {
    paddingHorizontal: theme.spacing.m,
  },
  bottomSheetBody: {
    marginBottom: theme.spacing.m,
  },
  bottomSheetTitle: {
    marginBottom: theme.spacing.s,
  },
  warningColor: {
    color: theme.color.status.negative,
  },
});

export default AddressValidation;
